<template>
  <section class="ls-upload-photos">
    <LsInputLabel
      v-if="label"
      :label="label"
      :sub-label="subLabel"
      :one-line-label="oneLineLabel"
      :collapse-content="collapseContent"
    />
    <BLoading :active.sync="compressionInProgress" />
    <BField :type="{ 'is-error': getValidation.invalid }" :message="getValidation.message">
      <BUpload
        v-model="localValue"
        v-validate="validation"
        accept="image/*"
        multiple
        drag-drop
        test-id="ls-upload-photos"
        :disabled="disabled"
        :name="name"
        expanded
      >
        <div class="upload-wrapper">
          <LsIcon v-if="!screenSize.desktop" type="add" size="sm" />
          <LsIcon v-else type="drag" :size-override="40" />
          <span class="drag-photos-here">
            {{ !screenSize.desktop ? 'ADD PHOTO' : 'Drag photos here or click to browse' }}
          </span>
        </div>
      </BUpload>
    </BField>
    <BCollapse class="panel" :open.sync="hasImages">
      <div class="notification pending-imgs">
        <div v-for="(file, idx) in localValue" :key="idx">
          <img :src="getImage(file)" />
          <LsButton
            :track-id="trackId"
            test-id="delete"
            icon="close"
            bg-color="primary"
            class="delete-btn"
            :disabled="disabled"
            @click="deleteDropFile(idx)"
          />
        </div>
      </div>
    </BCollapse>
  </section>
</template>

<script>
import VueTypes from 'vue-types';
import { mapState } from 'vuex';
import {
  lsI18nService,
  lsPropTypeHelpers,
  lsTrackingService,
  lsValidationService,
} from 'ls-vue-common';
import { LsInputLabel, LsButton, LsIcon } from '@/components/common';
import _ from 'lodash';
import imageCompression from 'browser-image-compression';
import { lsUploadPhotosHelper } from '@/helpers';

const OPTIONS = {
  useWebWorker: true,
  maxWidthOrHeight: 800,
  initialQuality: 0.2,
  fileType: 'image/jpeg',
};

const _compressImages = async (ref) => {
  ref.compressionInProgress = true;

  for (const idx in ref.value) {
    const file = ref.value[idx];
    if (!file.compressed) {
      const [fileName] = file.name.split('.');

      ref.value[idx] = await imageCompression(file, OPTIONS);
      ref.value[idx].name = `${fileName}.jpeg`;
      ref.value[idx].compressed = true;
    }
  }

  ref.compressionInProgress = false;
  ref.localValue.push(ref.localValue.pop());
};

export default {
  name: 'LsUploadPhotos',
  inject: ['$validator'],
  components: {
    LsButton,
    LsIcon,
    LsInputLabel,
  },
  props: {
    value: VueTypes.array.isRequired,
    trackId: VueTypes.custom(lsPropTypeHelpers.trackID).isRequired,
    name: VueTypes.string.isRequired,
    label: VueTypes.string,
    subLabel: VueTypes.string,
    oneLineLabel: VueTypes.bool.def(false),
    collapseContent: VueTypes.string,
    validation: VueTypes.object.def(_.constant(null)),
    disabled: VueTypes.bool.def(false),
    isGettingImagesFromOrder: VueTypes.bool.def(false),
  },
  data() {
    return {
      compressionInProgress: false,
    };
  },
  computed: {
    ...mapState(['screenSize']),
    localValue: {
      get() {
        return this.value;
      },
      async set(value) {
        lsTrackingService.track({
          event: 'attach',
          target: this.trackId,
          data: { value },
        });
        this.$emit('input', value);

        // touched flag not automatically set on this input
        this.$validator.flag(this.name, {
          touched: true,
        });

        if (this.hasImages) await _compressImages(this);
      },
    },
    hasImages() {
      return _.size(this.localValue) > 0;
    },
    getValidation() {
      if (!lsUploadPhotosHelper.isValidUpload(this.localValue) && !this.compressionInProgress)
        return {
          invalid: true,
          message: lsI18nService.translate('uploadPhotos.uploadLimitReached'),
        };

      return lsValidationService.getValidation.call(this);
    },
  },
  methods: {
    deleteDropFile(idx) {
      this.localValue.splice(idx, 1);
      this.$emit('input', this.localValue);
    },
    getImage(file) {
      // in the case where we are getting images from an existing onboarding order, use file.thumbnail_url to avoid errors with URL.createObjectURL
      return this.isGettingImagesFromOrder ? file.thumbnail_url : URL.createObjectURL(file);
    },
  },
};
</script>

<style lang="scss">
.ls-upload-photos {
  .upload .upload-draggable {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 0;
    background: $primary-light;
    padding: 20px;
    height: 70px;
    @media screen and (min-width: $desktop) {
      height: 110px;
    }
  }

  .upload-wrapper {
    display: flex;
    align-items: center;
    flex-direction: column;
  }

  .drag-photos-here {
    margin-top: 10px;
    color: $primary;
    font-weight: bold;
    @media screen and (min-width: $desktop) {
      font-weight: normal;
    }
  }

  .pending-imgs {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    div {
      flex: 0 0 auto;
      &:not(:first-child) {
        padding-left: 10px;
      }
      img {
        max-height: 70px;
        @media screen and (min-width: $desktop) {
          max-height: 110px;
        }
      }
    }
  }

  .delete-btn {
    margin-top: 2px;
    margin-left: -50px;
  }
}

.themed-app .ls-upload-photos {
  .upload .upload-draggable {
    background-color: $theme-color-primary-light;

    &:hover {
      background-color: $theme-color-primary-lighter;
    }
  }

  .drag-photos-here {
    color: $theme-color-primary;
  }

  .notification {
    background-color: $theme-color-gray-light;
  }
}
</style>
